import {
  AlertCircle,
  Calculator,
  ClipboardList,
  FileText,
  PackageCheck,
  Plus,
  Printer,
  RotateCcw,
  ScanLine,
  Tag,
  Truck,
  X
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../../../components/shared/Modal/Modal';
import { useToast } from '../../../../hooks/useToast';
import { findBookByCode, listBooks, updateBook } from '../../../../services/BooksService';
import { listCompanies } from '../../../../services/CompaniesService';
import { createGoodsReceiptWithLines } from '../../../../services/GoodsReceiptsService';
import { getPurchaseOrder, listPurchaseOrders } from '../../../../services/PurchaseOrdersService';
import { listSuppliers } from '../../../../services/SuppliersService';
import { listWarehouses } from '../../../../services/WarehousesService';
import { getActiveCompanyId } from '../../../../services/apiUtils';
import { Book, PurchaseOrder, PurchaseOrderLine, Society, Supplier, Warehouse } from '../../../../types';
import { formatMoney, formatQuantity, parseNumber } from '../PurchasesView/components/format';
import { ScreenSkeleton } from '../ScreenSkeleton';
import '../PurchasesView/PurchasesView.css';

type CodeField = 'ean' | 'sku' | 'isbn';

const CODE_LABELS: Record<CodeField, string> = {
  ean: 'EAN',
  sku: 'código Marcial Pons',
  isbn: 'ISBN'
};

interface ReceiptLine {
  key: string; // id de la línea de pedido
  orderId: string;
  poNumber: string;
  position: number;
  line: PurchaseOrderLine;
  currency: string;
  sku: string;
  isbn: string;
  quantity: string;
  warehouseId: string;
  location: string;
  pvp: string;
  originalPvp: number | null;
  result?: 'received' | 'failed';
  resultError?: string;
}

const pendingOf = (line: PurchaseOrderLine) => Math.max(line.quantityOrdered - line.quantityReceived, 0);

const orderDate = (o: PurchaseOrder) => o.issuedAt || o.createdAt || '';

const EMPTY_CODES: Record<CodeField, string> = { ean: '', sku: '', isbn: '' };

const CODE_FIELDS: CodeField[] = ['ean', 'sku', 'isbn'];

// Recepción de mercancía contra pedido: se escanea el libro (o se llama al
// pedido) y se recepciona en uno o varios almacenes. Los datos de facturación,
// la contabilización, la ubicación y la impresión de etiquetas se ven pero no
// hacen nada todavía.
export const GoodsReceiptScreen = () => {
  const { notify } = useToast();

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [companies, setCompanies] = useState<Society[]>([]);
  const [openOrders, setOpenOrders] = useState<PurchaseOrder[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(true);

  // Las líneas solo vienen en purchase_orders_get: se guardan para no pedir dos veces la misma orden.
  const orderCache = useRef<Record<string, PurchaseOrder>>({});

  const [supplierId, setSupplierId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [packageCount, setPackageCount] = useState('');
  const [isOrderListOpen, setIsOrderListOpen] = useState(false);
  const [codes, setCodes] = useState(EMPTY_CODES);
  const [quantityToReceive, setQuantityToReceive] = useState('1');

  const [hasInvoice, setHasInvoice] = useState(false);
  const [formInc, setFormInc] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceReference, setInvoiceReference] = useState('');
  const [freight, setFreight] = useState('');
  const [companyId, setCompanyId] = useState(() => {
    try {
      return getActiveCompanyId();
    } catch {
      return '';
    }
  });
  const [invoiceCurrency, setInvoiceCurrency] = useState('EUR');

  const [lines, setLines] = useState<ReceiptLine[]>([]);
  const [error, setError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [labelLines, setLabelLines] = useState<ReceiptLine[] | null>(null);

  const loadOpenOrders = async () => {
    const pages = await Promise.all([
      listPurchaseOrders({ status: 'approved', pageSize: 100 }),
      listPurchaseOrders({ status: 'partially_received', pageSize: 100 })
    ]);
    // De la más antigua a la más reciente: es el orden en que se sirven
    const orders = [...pages[0].data, ...pages[1].data].sort((a, b) => orderDate(a).localeCompare(orderDate(b)));
    setOpenOrders(orders);
    return orders;
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listSuppliers().then(list => { if (!cancelled) setSuppliers(list); }),
      listBooks().then(list => { if (!cancelled) setBooks(list); }),
      listWarehouses().then(list => { if (!cancelled) setWarehouses(list.filter(w => w.type === 'physical')); }),
      listCompanies().then(list => { if (!cancelled) setCompanies(list); }),
      loadOpenOrders()
    ])
      .catch(err => {
        console.error('Error loading goods receipt catalogs:', err);
        if (!cancelled) setError(err instanceof Error ? err.message : 'No se pudieron cargar los datos de la recepción.');
      })
      .then(() => { if (!cancelled) setCatalogsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const supplier = suppliers.find(s => s.id === supplierId);
  const supplierName = supplier?.name || openOrders.find(o => o.supplierId === supplierId)?.supplierName || '';
  const supplierOrders = openOrders.filter(o => !supplierId || o.supplierId === supplierId);
  const loadedOrderNumbers = Array.from(new Set(lines.map(l => l.poNumber)));
  const receivableLines = lines.filter(l => !l.result && parseNumber(l.quantity) > 0);
  const isLocked = isSaving || isSearching;

  const loadOrder = async (id: string): Promise<PurchaseOrder> => {
    if (!orderCache.current[id]) orderCache.current[id] = await getPurchaseOrder(id);
    return orderCache.current[id];
  };

  const toReceiptLine = (order: PurchaseOrder, index: number, quantity: number): ReceiptLine => {
    const line = order.lines[index];
    const book = line.bookId ? books.find(b => b.id === line.bookId) : undefined;
    return {
      key: line.id,
      orderId: order.id,
      poNumber: order.poNumber,
      position: index + 1,
      line,
      currency: order.currency || 'EUR',
      sku: line.sku || book?.sku || '',
      isbn: book?.isbn || '',
      quantity: quantity > 0 ? String(quantity) : '',
      warehouseId: warehouses.some(w => w.id === order.warehouseId) ? order.warehouseId : '',
      location: '',
      pvp: book ? String(book.price) : '',
      originalPvp: book ? book.price : null
    };
  };

  // Una línea que ya está en la tabla suma la cantidad; si ya se recepcionó, se
  // sustituye por la nueva (con lo pendiente actualizado); si no está, va al final
  const addLines = (incoming: ReceiptLine[]) => {
    setLines(prev => {
      const next = [...prev];
      incoming.forEach(line => {
        const i = next.findIndex(l => l.key === line.key);
        if (i === -1) next.push(line);
        else if (next[i].result) next[i] = line;
        else next[i] = { ...next[i], quantity: String(parseNumber(next[i].quantity) + parseNumber(line.quantity)) };
      });
      return next;
    });
  };

  const handleCodeSearch = async (field: CodeField) => {
    const value = codes[field].trim();
    if (!value || isLocked) return;
    setError('');

    const book = findBookByCode(books, value, field === 'sku' ? 'sku' : 'isbn');
    if (!book) {
      setError(`No hay ningún libro con ${CODE_LABELS[field]} ${value}.`);
      return;
    }

    const quantity = Math.max(1, Math.round(parseNumber(quantityToReceive, 1)));
    const inTable = (lineId: string) => parseNumber(lines.find(l => l.key === lineId && !l.result)?.quantity || '0');

    setIsSearching(true);
    try {
      // Sin filtro por libro en el backend: se abren los pedidos uno a uno hasta dar con una línea pendiente
      for (const header of supplierOrders) {
        const order = await loadOrder(header.id);
        const index = order.lines.findIndex(l => l.bookId === book.id && pendingOf(l) - inTable(l.id) > 0);
        if (index !== -1) {
          addLines([toReceiptLine(order, index, quantity)]);
          setSupplierId(order.supplierId);
          setCodes(EMPTY_CODES);
          setQuantityToReceive('1');
          return;
        }
      }
      setError(`"${book.title}" no tiene unidades pendientes en ningún pedido abierto${supplierName ? ` de ${supplierName}` : ''}.`);
    } catch (err) {
      console.error('Error searching purchase orders:', err);
      setError(err instanceof Error ? err.message : 'No se pudieron consultar los pedidos.');
    } finally {
      setIsSearching(false);
    }
  };

  // El botón hace lo mismo que Intro en el primer código que esté relleno
  const handleAddClick = () => {
    const field = CODE_FIELDS.find(f => codes[f].trim());
    if (field) handleCodeSearch(field);
    else setError('Escriba un código EAN, Marcial Pons o ISBN.');
  };

  const handleAddOrder = async (header: PurchaseOrder) => {
    if (isLocked) return;
    setError('');
    if (supplierId && header.supplierId !== supplierId) {
      setError(`El pedido ${header.poNumber} es de ${header.supplierName}, no de ${supplierName}.`);
      return;
    }
    setIsSearching(true);
    try {
      const order = await loadOrder(header.id);
      const pending = order.lines
        .map((line, index) => ({ line, index }))
        .filter(({ line }) => pendingOf(line) > 0 && !lines.some(l => l.key === line.id && !l.result));
      addLines(pending.map(({ index }) => toReceiptLine(order, index, 0)));
      setSupplierId(order.supplierId);
      setOrderNumber('');
      setIsOrderListOpen(false);
    } catch (err) {
      console.error('Error loading purchase order:', err);
      setError(err instanceof Error ? err.message : 'No se pudo cargar el pedido.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleOrderNumber = () => {
    const value = orderNumber.trim().toLowerCase();
    if (!value) return;
    const header = openOrders.find(o => o.poNumber.toLowerCase() === value);
    if (!header) {
      setError(`No hay ningún pedido aprobado o parcialmente recibido con el número ${orderNumber.trim()}.`);
      return;
    }
    handleAddOrder(header);
  };

  const updateLine = (key: string, changes: Partial<ReceiptLine>) => {
    setLines(prev => prev.map(l => (l.key === key ? { ...l, ...changes } : l)));
  };

  const removeLine = (key: string) => {
    setLines(prev => {
      const next = prev.filter(l => l.key !== key);
      if (next.length === 0) setSupplierId('');
      return next;
    });
  };

  const handleClear = () => {
    setLines([]);
    setSupplierId('');
    setOrderNumber('');
    setDeliveryNote('');
    setPackageCount('');
    setCodes(EMPTY_CODES);
    setQuantityToReceive('1');
    setHasInvoice(false);
    setFormInc(false);
    setInvoiceDate('');
    setInvoiceReference('');
    setFreight('');
    setInvoiceCurrency('EUR');
    setError('');
  };

  // Un albarán por pedido y almacén: así una misma entrada puede repartirse entre almacenes
  const handleReceive = async () => {
    if (isLocked || receivableLines.length === 0) return;
    if (receivableLines.some(l => !l.warehouseId)) {
      setError('Elija el almacén de todas las líneas que se van a recepcionar.');
      return;
    }
    // Como en Compras: el backend rechazaría la línea, pero el albarán ya quedaría creado
    const exceeded = receivableLines.find(l => parseNumber(l.quantity) > pendingOf(l.line));
    if (exceeded) {
      setError(`"${exceeded.line.bookTitle || exceeded.line.description}": no se pueden recibir más de ${formatQuantity(pendingOf(exceeded.line))} unidades pendientes.`);
      return;
    }
    setError('');
    setIsSaving(true);

    const groups: Record<string, ReceiptLine[]> = {};
    receivableLines.forEach(l => {
      const groupKey = `${l.orderId}|${l.warehouseId}`;
      groups[groupKey] = [...(groups[groupKey] || []), l];
    });

    const results: Record<string, Pick<ReceiptLine, 'result' | 'resultError'>> = {};
    let receiptsCreated = 0;
    for (const groupLines of Object.values(groups)) {
      try {
        const { failedLines } = await createGoodsReceiptWithLines(
          {
            purchaseOrderId: groupLines[0].orderId,
            warehouseId: groupLines[0].warehouseId,
            deliveryNoteNumber: deliveryNote.trim() || undefined,
            packageCount: packageCount.trim() === '' ? null : Math.max(0, Math.round(parseNumber(packageCount)))
          },
          groupLines.map(l => ({ purchaseOrderLineId: l.key, quantityReceived: parseNumber(l.quantity) }))
        );
        receiptsCreated += 1;
        groupLines.forEach(l => {
          const failed = failedLines.find(f => f.purchaseOrderLineId === l.key);
          results[l.key] = failed ? { result: 'failed', resultError: failed.error } : { result: 'received' };
        });
      } catch (err) {
        console.error('Error registering goods receipt:', err);
        const message = err instanceof Error ? err.message : 'No se pudo registrar la recepción.';
        groupLines.forEach(l => { results[l.key] = { result: 'failed', resultError: message }; });
      }
      delete orderCache.current[groupLines[0].orderId];
    }

    // PVP: se corrige en la ficha del libro, una vez por libro, solo si la línea entró
    const pvpChanges = new Map<string, number>();
    receivableLines.forEach(l => {
      const price = parseNumber(l.pvp, NaN);
      if (results[l.key].result === 'received' && l.line.bookId && l.originalPvp !== null && !isNaN(price) && price !== l.originalPvp) {
        pvpChanges.set(l.line.bookId, price);
      }
    });
    const pvpUpdated = new Set<string>();
    const pvpErrors: string[] = [];
    for (const [bookId, price] of Array.from(pvpChanges.entries())) {
      try {
        const updated = await updateBook(bookId, { price });
        pvpUpdated.add(bookId);
        setBooks(prev => prev.map(b => (b.id === bookId ? { ...b, price: updated.price } : b)));
      } catch (err) {
        console.error('Error updating book price:', err);
        pvpErrors.push(books.find(b => b.id === bookId)?.title || bookId);
      }
    }

    const nextLines = lines.map(l => {
      if (!results[l.key]) return l;
      const pvpSaved = Boolean(l.line.bookId && pvpUpdated.has(l.line.bookId));
      return { ...l, ...results[l.key], originalPvp: pvpSaved ? parseNumber(l.pvp) : l.originalPvp };
    });
    setLines(nextLines);

    const failedCount = Object.values(results).filter(r => r.result === 'failed').length;
    if (failedCount > 0 || pvpErrors.length > 0) {
      const parts = [];
      if (failedCount > 0) parts.push(`${failedCount} línea(s) no entraron`);
      if (pvpErrors.length > 0) parts.push(`no se pudo cambiar el PVP de ${pvpErrors.join(', ')}`);
      notify('Aviso', `Recepción registrada en ${receiptsCreated} albarán(es), pero ${parts.join(' y ')}.`, 'warning');
    } else {
      notify('Éxito', `Recepción registrada en ${receiptsCreated} albarán(es)${pvpUpdated.size > 0 ? ` y ${pvpUpdated.size} PVP actualizado(s)` : ''}.`, 'info');
    }

    try {
      await loadOpenOrders();
    } catch (err) {
      console.error('Error reloading purchase orders:', err);
    }
    setIsSaving(false);

    const received = nextLines.filter(l => results[l.key]?.result === 'received');
    if (received.length > 0) setLabelLines(received);
  };

  const warehouseName = (id: string) => warehouses.find(w => w.id === id)?.name || '—';

  const lineStatus = (l: ReceiptLine) => {
    if (l.result === 'received') return <span className="module-badge received">Recibida</span>;
    if (l.result === 'failed') return <span className="module-badge cancelled" title={l.resultError}>Error</span>;
    const quantity = parseNumber(l.quantity);
    const pending = pendingOf(l.line);
    if (quantity > pending) return <span className="module-badge overdue">Excede</span>;
    if (quantity === 0) return <span className="module-badge pending">Pendiente</span>;
    if (quantity < pending) return <span className="module-badge partially_received">Parcial</span>;
    return <span className="module-badge completed">Completa</span>;
  };

  const codeInput = (field: CodeField, label: string) => (
    <div className="module-form-group">
      <label className="module-form-label">{label}</label>
      <input
        type="text"
        value={codes[field]}
        onChange={(e) => { const val = e.target.value; setCodes(prev => ({ ...prev, [field]: val })); }}
        onKeyDown={(e) => { if (e.key === 'Enter') handleCodeSearch(field); }}
        disabled={catalogsLoading || isLocked}
        className="module-form-input"
      />
    </div>
  );

  return (
    <div className="module-screen">
      <div className="module-screen-actions">
        <button type="button" onClick={handleReceive} disabled={isLocked || receivableLines.length === 0} className="module-form-submit-btn">
          <PackageCheck size={12} /> {isSaving ? 'Recepcionando...' : 'Recepcionar'}
        </button>
        <button type="button" disabled title="Pendiente de endpoint" className="module-form-cancel-btn">
          <Calculator size={12} /> Contabilizar factura
        </button>
        <button type="button" onClick={() => setLabelLines(lines.filter(l => parseNumber(l.quantity) > 0))} disabled={!lines.some(l => parseNumber(l.quantity) > 0)} className="module-form-cancel-btn">
          <Tag size={12} /> Etiquetas
        </button>
        <button type="button" onClick={handleClear} disabled={isLocked} className="module-form-cancel-btn">
          <RotateCcw size={12} /> Limpiar
        </button>
      </div>

      {error && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      {catalogsLoading ? <ScreenSkeleton /> : (
      <div className="module-screen-grid">
        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><Truck size={14} className="module-panel-title-icon" /> Datos del proveedor</h3>
          {supplierId ? (
            <dl className="module-screen-data">
              <dt>Proveedor</dt><dd>{supplierName}{supplier?.cif ? ` · ${supplier.cif}` : ''}</dd>
              <dt>Teléfono</dt><dd>{supplier?.phone || '—'}</dd>
              <dt>Fax</dt><dd>—</dd>
            </dl>
          ) : (
            <p className="module-screen-hint">Se rellena con el primer pedido que se recepcione.</p>
          )}
        </section>

        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><ClipboardList size={14} className="module-panel-title-icon" /> Pedidos del proveedor</h3>
          <div className="module-screen-inline">
            <div className="module-form-group">
              <label className="module-form-label">Número de pedido</label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleOrderNumber(); }}
                disabled={catalogsLoading || isLocked}
                className="module-form-input"
              />
            </div>
            <button type="button" onClick={handleOrderNumber} disabled={catalogsLoading || isLocked || !orderNumber.trim()} className="module-form-cancel-btn">
              Cargar
            </button>
            <button type="button" onClick={() => setIsOrderListOpen(open => !open)} disabled={catalogsLoading || isLocked} className="module-form-cancel-btn">
              Pedidos
            </button>
          </div>
          {isOrderListOpen && (
            <div className="warehouses-view__receipt-order-list">
              {supplierOrders.length === 0 ? (
                <span className="module-screen-hint">No hay pedidos abiertos{supplierName ? ` de ${supplierName}` : ''}</span>
              ) : supplierOrders.map(o => (
                <button key={o.id} type="button" onClick={() => handleAddOrder(o)} className="warehouses-view__receipt-order">
                  <strong>{o.poNumber}</strong>
                  <span>{o.supplierName} · {o.warehouseName || `#${o.warehouseId}`}</span>
                  <span className={`module-badge ${o.status}`}>{o.status === 'approved' ? 'Aprobada' : 'Recibida parcialmente'}</span>
                </button>
              ))}
            </div>
          )}
          <div className="module-screen-fields">
            <div className="module-form-group">
              <label className="module-form-label">Nota de entrega (nº albarán)</label>
              <input type="text" value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} disabled={isLocked} className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Bultos</label>
              <input type="number" min="0" step="1" value={packageCount} onChange={(e) => setPackageCount(e.target.value)} disabled={isLocked} className="module-form-input" />
            </div>
          </div>
          {loadedOrderNumbers.length > 0 && (
            <div className="warehouses-view__receipt-chips">
              {loadedOrderNumbers.map(n => <span key={n} className="module-badge approved">{n}</span>)}
            </div>
          )}
        </section>

        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><ScanLine size={14} className="module-panel-title-icon" /> Código libro</h3>
          <div className="module-screen-fields">
            {codeInput('ean', 'Código EAN')}
            {codeInput('sku', 'Código Marcial Pons')}
            {codeInput('isbn', 'Código ISBN')}
            <div className="module-form-group">
              <label className="module-form-label">Cant. a recibir</label>
              <input
                type="number"
                min="1"
                step="1"
                value={quantityToReceive}
                onChange={(e) => setQuantityToReceive(e.target.value)}
                disabled={catalogsLoading || isLocked}
                className="module-form-input"
              />
            </div>
          </div>
          <div className="module-screen-actions">
            <button type="button" onClick={handleAddClick} disabled={catalogsLoading || isLocked} className="module-create-btn">
              <Plus size={12} /> Añadir
            </button>
          </div>
          <p className="module-screen-hint">
            {catalogsLoading ? 'Cargando catálogo y pedidos...' : isSearching ? 'Buscando en los pedidos abiertos...' : 'Escanee o escriba el código y pulse Intro o Añadir.'}
          </p>
        </section>

        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><FileText size={14} className="module-panel-title-icon" /> Datos de facturación</h3>
          <div className="module-screen-checks">
            <label><input type="checkbox" checked={hasInvoice} onChange={(e) => setHasInvoice(e.target.checked)} /> Factura</label>
            <label><input type="checkbox" checked={formInc} onChange={(e) => setFormInc(e.target.checked)} /> Form. Inc.</label>
            <button type="button" disabled title="Pendiente de endpoint" className="module-form-cancel-btn">
              <Truck size={12} /> Fact. transporte
            </button>
          </div>
          <div className="module-screen-fields">
            <div className="module-form-group">
              <label className="module-form-label">Fecha factura</label>
              <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Referencia</label>
              <input type="text" value={invoiceReference} onChange={(e) => setInvoiceReference(e.target.value)} className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Portes</label>
              <input type="number" min="0" step="0.01" value={freight} onChange={(e) => setFreight(e.target.value)} className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Div. factura</label>
              <input type="text" maxLength={3} value={invoiceCurrency} onChange={(e) => setInvoiceCurrency(e.target.value.toUpperCase())} className="module-form-input" />
            </div>
            <div className="module-form-group module-screen-span">
              <label className="module-form-label">Sociedad</label>
              <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="module-form-select">
                <option value="">Seleccione...</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
        </section>
      </div>
      )}

      <section className="module-panel module-screen-block">
        <h3 className="module-panel-title"><PackageCheck size={14} className="module-panel-title-icon" /> Líneas a recepcionar</h3>
        <div className="purchases-view__lines-wrapper">
          <table className="purchases-view__lines-table purchases-view__lines-table--readonly module-screen-table">
            <thead>
              <tr>
                <th>St</th>
                <th>Código MP</th>
                <th>Título del libro</th>
                <th>Pedido</th>
                <th className="right">Posición</th>
                <th className="right">Cantidad</th>
                <th>U</th>
                <th className="right">Precio</th>
                <th>Moneda</th>
                <th className="right">Descuento</th>
                <th className="right">IVA</th>
                <th>Almacén</th>
                <th>Ubicación</th>
                <th className="right">PVP</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={15} className="purchases-view__lines-empty">Escanee un libro o llame a un pedido para empezar.</td>
                </tr>
              ) : lines.map(l => {
                const done = l.result === 'received';
                const pvpChanged = l.originalPvp !== null && l.pvp !== '' && parseNumber(l.pvp) !== l.originalPvp;
                return (
                  <tr key={l.key} className={done ? 'purchases-view__line-done' : ''}>
                    <td>{lineStatus(l)}</td>
                    <td>{l.sku || '—'}</td>
                    <td>
                      <div className="purchases-view__line-desc">{l.line.bookTitle || l.line.description}</div>
                      {l.isbn && <div className="purchases-view__line-sub">{l.isbn}</div>}
                    </td>
                    <td>{l.poNumber}</td>
                    <td className="right">{l.position}</td>
                    <td className="right">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={l.quantity}
                        onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                        disabled={done || isLocked}
                        placeholder="0"
                        className="module-form-input right"
                      />
                      {!done && <div className="purchases-view__line-sub">Pend. {formatQuantity(pendingOf(l.line))}</div>}
                    </td>
                    <td>{l.line.unitOfMeasure || '—'}</td>
                    <td className="right">{formatMoney(l.line.unitPrice, l.currency)}</td>
                    <td>{l.currency}</td>
                    <td className="right">{formatQuantity(l.line.discountPct)}%</td>
                    <td className="right">{formatQuantity(l.line.taxRate)}%</td>
                    <td>
                      <select
                        value={l.warehouseId}
                        onChange={(e) => updateLine(l.key, { warehouseId: e.target.value })}
                        disabled={done || isLocked}
                        className="module-form-select"
                      >
                        <option value="">Seleccione...</option>
                        {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={l.location}
                        onChange={(e) => updateLine(l.key, { location: e.target.value })}
                        disabled={done || isLocked}
                        className="module-form-input"
                      />
                    </td>
                    <td className="right">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={l.pvp}
                        onChange={(e) => updateLine(l.key, { pvp: e.target.value })}
                        disabled={done || isLocked || l.originalPvp === null}
                        className={`module-form-input right ${pvpChanged ? 'module-screen-input--changed' : ''}`}
                      />
                      {pvpChanged && <div className="purchases-view__line-sub">Antes {formatMoney(l.originalPvp || 0)}</div>}
                    </td>
                    <td>
                      {!done && (
                        <button type="button" onClick={() => removeLine(l.key)} disabled={isLocked} className="module-screen-remove" title="Quitar línea">
                          <X size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        isOpen={labelLines !== null}
        type="info"
        title="Etiquetas"
        message="Una etiqueta por línea, con el almacén y la ubicación a los que va el libro."
        cancelText="Cerrar"
        onCancel={() => setLabelLines(null)}
      >
        <div className="warehouses-view__receipt-labels">
          {(labelLines || []).map(l => (
            <div key={l.key} className="warehouses-view__receipt-label">
              <strong>{l.line.bookTitle || l.line.description}</strong>
              <span>{l.isbn || l.sku || '—'}</span>
              <span>PVP {l.pvp ? formatMoney(parseNumber(l.pvp)) : '—'}</span>
              <span>{warehouseName(l.warehouseId)} · {l.location || 'Sin ubicación'}</span>
              <span className="warehouses-view__receipt-label-qty">× {formatQuantity(parseNumber(l.quantity))}</span>
            </div>
          ))}
        </div>
        <button type="button" disabled title="Pendiente de endpoint" className="module-form-cancel-btn warehouses-view__receipt-print">
          <Printer size={12} /> Imprimir
        </button>
      </Modal>
    </div>
  );
};
